/**
 * Formats a date-time string to 'Month day year HH:mm' format with optional veterinarian name and localization.
 * Matches the Blazor component behavior for consistency.
 * @param dateTimeString - The date-time string to format.
 * @param veterinarianName - Optional. Name of the veterinarian.
 * @param language - The language tag of the site (e.g., 'en', 'sv-SE'). Required, so that the
 * date is never shown in a fallback language by mistake.
 * @returns Formatted date-time string.
 */
export const formatAppointmentDateTime = (
  dateTimeString: string,
  language: string,
  veterinarianName?: string
): string => {
  const date = new Date(dateTimeString);

  const datePart = date.toLocaleDateString(language, {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const timePart = `${hours}:${minutes}`;

  const dateTime = `${datePart} ${timePart}`;

  return veterinarianName ? `${dateTime} ${veterinarianName}` : dateTime;
};

/**
 * Formats a date-time string to 'Month day year' format with localization.
 * @param dateTimeString - The date-time string to format.
 * @param language - The BCP47 language tag of the site (e.g., 'en', 'sv'). Required, so that the
 * date is never shown in a fallback language by mistake.
 * @returns Formatted date-time string.
 */
export const formatAppointmentDate = (
  dateTimeString: string,
  language: string
): string => {
  const date = new Date(dateTimeString);

  return date.toLocaleDateString(language, {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};

/**
 * Formats just the date portion (yyyy-MM-dd).
 * @param dateTimeString - The date-time string to format.
 * @returns Formatted date string.
 */
export const formatAppointmentDateShorthand = (dateTimeString: string): string => {
  const date = new Date(dateTimeString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Formats just the time portion (HH:mm).
 * @param dateTimeString - The date-time string to format.
 * @returns Formatted time string.
 */
export const formatAppointmentTime = (dateTimeString: string): string => {
  const date = new Date(dateTimeString);

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};
